import {combineEpics, Epic} from 'redux-observable';
import {from, of} from 'rxjs';
import {
  switchMap,
  map,
  startWith,
  catchError,
  filter
} from 'rxjs/operators';
import {isOfType} from 'typesafe-actions';
import { Position, Toaster, Intent } from '@blueprintjs/core';

import {
  RolesAction,
  RolesActionTypes,
  loadedRoles,
  loadingRoles,
  loadingRolesFailed,
  loadedRole,
  loadingRole,
  loadingRoleFailed,
  createdRole,
  creatingRole,
  creatingRoleFailed,
  updatedRole,
  updatingRole,
  updatingRoleFailed,
  deletedRole,
  deletingRole,
  deletingRoleFailed,
  assignedRole,
  assigningRole,
  assigningRoleFailed,
  revokedRole,
  revokingRole,
  revokingRoleFailed,
} from '../actions/roleActions';
import {IState} from '../reducers';
import {getRoles, getRole, createRole, updateRole, deleteRole, assignRole, revokeRole} from '../../services/rolesApi';

export const roleToaster = Toaster.create({
  className: 'role-toaster',
  position: Position.TOP
});

const loadRolesEpic: Epic<RolesAction, RolesAction, IState> = (
  action$,
  state$
) =>
  action$.pipe(
    filter(isOfType(RolesActionTypes.LOAD_ROLES)),
    switchMap((action: any) =>
      from(getRoles()).pipe(
        map((response: any) => loadedRoles(
          response.result,
          action.payload.reload,
          action.payload.page_size,
        )),
        startWith(loadingRoles()),
        catchError(() => of(loadingRolesFailed()))
      )
    )
  );

const loadRoleEpic: Epic<RolesAction, RolesAction, IState> = (
  action$: any,
  state$: any
) =>
  action$.pipe(
    filter(isOfType(RolesActionTypes.LOAD_ROLE)),
    switchMap((action: any) =>
      from(getRole(action.payload)).pipe(
        map((response: any) => loadedRole(response.result)),
        startWith(loadingRole()),
        catchError(() => of(loadingRoleFailed()))
      )
    )
  );

const createRoleEpic: Epic<RolesAction, RolesAction, IState> = (
  action$: any,
  state$: any
) =>
  action$.pipe(
    filter(isOfType(RolesActionTypes.CREATE_ROLE)),
    switchMap((action: any) =>
      from(createRole(action.payload.name)).pipe(
        map((response: any) => createdRole({
          id: response.id,
          ...response.result,
        })),
        startWith(creatingRole()),
        catchError((error: any) => {
          const message = error.response?.data?.message || "";

          roleToaster.show({
            message: `An error occurred while creating role: ${message}.`,
            intent: Intent.DANGER,
          });

          return of(creatingRoleFailed());
        })
      )
    )
  );

const updateRoleEpic: Epic<RolesAction, RolesAction, IState> = (
  action$: any,
  state$: any
) =>
  action$.pipe(
    filter(isOfType(RolesActionTypes.UPDATE_ROLE)),
    switchMap((action: any) =>
      from(updateRole(action.payload.uid, action.payload.name)).pipe(
        map((response: any) => updatedRole({
          uid: action.payload.uid,
          ...response.result,
        })),
        startWith(updatingRole()),
        catchError((error: any) => {
          const message = error.response?.data?.message || "";

          roleToaster.show({
            message: `An error occurred while updating role: ${message}.`,
            intent: Intent.DANGER,
          });

          return of(updatingRoleFailed());
        })
      )
    )
  );

const deleteRoleEpic: Epic<RolesAction, RolesAction, IState> = (
  action$: any,
  state$: any
) =>
  action$.pipe(
    filter(isOfType(RolesActionTypes.DELETE_ROLE)),
    switchMap((action: any) =>
      from(deleteRole(action.payload)).pipe(
        map((response: any) => deletedRole(action.payload)),
        startWith(deletingRole()),
        catchError((error: any) => {
          const message = error.response?.data?.message || "";

          roleToaster.show({
            message: `An error occurred while deleting role: ${message}.`,
            intent: Intent.DANGER,
          });

          return of(deletingRoleFailed());
        })
      )
    )
  );

const assignRoleEpic: Epic<RolesAction, RolesAction, IState> = (
  action$: any,
  state$: any
) =>
  action$.pipe(
    filter(isOfType(RolesActionTypes.ASSIGN_ROLE)),
    switchMap((action: any) => {
      return from(assignRole(action.payload.uid, action.payload.usersUid)).pipe(
        map((response: any) => assignedRole(response.result)),
        startWith(assigningRole()),
        catchError((error: any) => {
          const message = error.response?.data?.message || "";

          roleToaster.show({
            message: `An error occurred while assigning the role to selected users: ${message}.`,
            intent: Intent.DANGER,
          });

          return of(assigningRoleFailed());
        })
      )
    })
  );

const revokeRoleEpic: Epic<RolesAction, RolesAction, IState> = (
  action$: any,
  state$: any
) =>
  action$.pipe(
    filter(isOfType(RolesActionTypes.REVOKE_ROLE)),
    switchMap((action: any) => {
      return from(revokeRole(action.payload.uid, action.payload.usersUid)).pipe(
        map((response: any) => revokedRole(action.payload)),
        startWith(revokingRole()),
        catchError((error: any) => {
          const message = error.response?.data?.message || "";

          roleToaster.show({
            message: `An error occurred while removing the role from user: ${message}.`,
            intent: Intent.DANGER,
          });

          return of(revokingRoleFailed());
        })
      )
    })
  );

export default combineEpics(
  loadRolesEpic,
  loadRoleEpic,
  createRoleEpic,
  updateRoleEpic,
  deleteRoleEpic,
  assignRoleEpic,
  revokeRoleEpic,
);
